import { ClientEvent, EventType, KnownMembership, MatrixClient, MatrixEvent, Room, RoomEvent } from 'matrix-js-sdk';
import { ICreateRoomOpts } from 'matrix-js-sdk/lib/@types/requests';

/**
 * Erstellt einen Raum und wartet, bis er im SDK verfügbar ist
 * @param client MatrixClient
 * @param options createRoom Optionen
 * @param timeoutMs Timeout in ms (Standard 5000)
 * @returns Promise<Room>
 */
export async function createRoomAndWait(
  client: MatrixClient,
  options: ICreateRoomOpts,
  timeoutMs = 5000
): Promise<Room> {
  // Raum erstellen
  const response = await client.createRoom(options);
  const roomId = response.room_id;

  // Prüfen, ob der Room schon da ist
  const room = client.getRoom(roomId);
  if (room) {
    return room;
  }

  // Warten auf das Room-Event
  return new Promise<Room>((resolve, reject) => {
    const handler = (newRoom: Room): void => {
      if (newRoom.roomId === roomId) {
        cleanup();
        resolve(newRoom);
      }
    };

    const cleanup = (): void => {
      client.removeListener(ClientEvent.Room, handler);
      clearTimeout(timeoutId);
    };

    client.on(ClientEvent.Room, handler);

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Room ${roomId} did not appear in time`));
    }, timeoutMs);
  });
}

/**
 * Vergisst einen Raum und wartet, bis er im SDK entfernt wurde.
 * @param client MatrixClient
 * @param roomId ID des zu vergessenden Raums
 * @param timeoutMs Timeout in ms (Standard 5000)
 * @returns Promise<void>
 */
export async function forgetRoomAndWait(client: MatrixClient, roomId: string, timeoutMs = 5000): Promise<void> {
  // Raum vergessen
  await client.forget(roomId);

  // Wenn Room schon weg ist, fertig
  if (!client.getRoom(roomId)) {
    return;
  }

  return new Promise<void>((resolve, reject) => {
    const handler = (removedRoomId: string): void => {
      if (removedRoomId === roomId) {
        cleanup();
        resolve();
      }
    };

    const cleanup = (): void => {
      client.removeListener(ClientEvent.DeleteRoom, handler);
      clearTimeout(timeoutId);
    };

    client.on(ClientEvent.DeleteRoom, handler);

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Room ${roomId} was not removed in time`));
    }, timeoutMs);
  });
}

/**
 * Lädt mehrere Benutzer in einen Raum ein und wartet,
 * bis die Invites auch im SDK-State angekommen sind.
 *
 * @param client MatrixClient
 * @param roomId Raum-ID
 * @param userIds Array von User-IDs, die eingeladen werden sollen
 * @param timeoutMs Timeout in ms (Standard 10s)
 */
export async function inviteAndWaitAll(
  client: MatrixClient,
  roomId: string,
  userIds: string[],
  timeoutMs = 10000
): Promise<void> {
  // 1. Invites abschicken (alle Promises parallel)
  await Promise.all(userIds.map(userId => client.invite(roomId, userId)));

  const room = client.getRoom(roomId);
  if (!room) {
    throw new Error(`Room ${roomId} not found in SDK`);
  }

  // 2. Prüfen, ob schon alle Invites im Room-State sind
  const invited = new Set(room.getMembersWithMembership(KnownMembership.Invite).map(m => m.userId));
  const missing = userIds.filter(u => !invited.has(u));
  if (missing.length === 0) {
    return; // schon alles da
  }

  // 3. Auf weitere Invite-Events warten
  await new Promise<void>((resolve, reject) => {
    const stillMissing = new Set(missing);

    const handler = (event: MatrixEvent, eventRoom: Room | undefined): void => {
      if (!eventRoom || eventRoom.roomId !== roomId) return;
      if (event.getType() !== EventType.RoomMember) return;
      if (event.getContent().membership !== KnownMembership.Invite) return;

      const invitedUser = event.getStateKey(); // userId des Invited
      if (invitedUser && stillMissing.has(invitedUser)) {
        stillMissing.delete(invitedUser);
        if (stillMissing.size === 0) {
          cleanup();
          resolve();
        }
      }
    };

    const cleanup = (): void => {
      client.removeListener(RoomEvent.Timeline, handler);
      clearTimeout(timeoutId);
    };

    client.on(RoomEvent.Timeline, handler);

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Invites did not appear in time: ${Array.from(stillMissing).join(', ')}`));
    }, timeoutMs);
  });
}

/**
 * Joint einem Raum bei und wartet, bis der Client sich selbst im Raum-Objekt
 * als 'join' sieht.
 *
 * @param client MatrixClient
 * @param roomId Die Raum-ID
 * @param timeoutMs Timeout in ms (Default: 10s)
 */
export async function joinRoomAndWait(client: MatrixClient, roomId: string, timeoutMs = 10000): Promise<Room> {
  const userId = client.getUserId();
  // 1. Join auslösen
  await client.joinRoom(roomId);

  // 2. Room holen
  let room = client.getRoom(roomId);
  // Room ist evtl. noch nicht in client.rooms
  if (userId) {
    room ??= new Room(roomId, client, userId);
  }

  // 3. Prüfen, ob wir schon drin sind
  if (room && userId) {
    const me = room.getMember(userId);
    if (me?.membership === KnownMembership.Join) {
      return room; // schon gejoint
    }
  }

  // 4. Warten auf Join-Event
  return new Promise<Room>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Timeout: User ${userId} did not join ${roomId} in time`));
    }, timeoutMs);

    const handler = (event: MatrixEvent, eventRoom: Room | undefined): void => {
      if (!eventRoom || eventRoom.roomId !== roomId) return;
      if (event.getType() !== EventType.RoomMember) return;
      if (event.getStateKey() !== userId) return;

      const membership = event.getContent().membership;
      if (membership === KnownMembership.Join) {
        cleanup();
        resolve(eventRoom);
      }
    };

    const cleanup = (): void => {
      client.removeListener(RoomEvent.Timeline, handler);
      clearTimeout(timeoutId);
    };

    client.on(RoomEvent.Timeline, handler);
  });
}

/**
 * Verlässt einen Raum und wartet, bis der Client sich selbst im Room-Objekt
 * als 'leave' sieht oder der Raum aus dem Client verschwindet.
 *
 * @param client MatrixClient
 * @param roomId Die Raum-ID
 * @param timeoutMs Timeout in ms (Default: 10s)
 * @returns den aktualisierten Room (oder null, wenn er aus dem Client verschwunden ist)
 */
export async function leaveRoomAndWait(client: MatrixClient, roomId: string, timeoutMs = 10000): Promise<Room | null> {
  // 1. Leave auslösen
  await client.leave(roomId);

  const userId = client.getUserId();
  const room = client.getRoom(roomId);

  if (!userId || !room) {
    return null;
  }

  if (room.getMember(userId)?.membership === KnownMembership.Leave) {
    return room;
  }

  // 3. Warten auf Leave-Event
  return new Promise<Room | null>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Timeout: User ${userId} did not leave ${roomId} in time`));
    }, timeoutMs);

    const handler = (event: MatrixEvent, eventRoom: Room | undefined): void => {
      // Wenn der Raum gar nicht mehr existiert → fertig
      const currentRoom = client.getRoom(roomId);
      if (!currentRoom) {
        cleanup();
        resolve(null); // Room aus Client entfernt
        return;
      }

      if (!eventRoom || eventRoom.roomId !== roomId) return;
      if (event.getType() !== EventType.RoomMember) return;
      if (event.getStateKey() !== userId) return;

      const membership = event.getContent().membership;
      if (membership === KnownMembership.Leave) {
        cleanup();
        resolve(currentRoom);
      }
    };

    const cleanup = (): void => {
      client.removeListener(RoomEvent.Timeline, handler);
      clearTimeout(timeoutId);
    };

    client.on(RoomEvent.Timeline, handler);
  });
}
